import rollbar from "@utils/rollbar";
import { tpTheme } from "@trainingpeaks/designtokens";
import { Subscription, SubscriptionStatus } from "@appTypes/index";
import {
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_SOURCES,
  SUBSCRIPTION_PRODUCT_TYPES,
  PRODUCT_DURATIONS,
  RECURRING_INTERVALS,
} from "@constants/subscriptions";
import formatDate from "@utils/formatDate";
import createStripeBillingPortalSession from "@api/createStripeBillingPortalSession";
import cancelStripeSubscription from "@api/cancelStripeSubscription";
import cancelZuoraAthletePremiumSubscription from "@api/cancelZuoraAthletePremiumSubscription";
import enableStripeSubscriptionAutoRenewal from "@api/enableStripeSubscriptionAutoRenewal";
import enableZuoraSubscriptionAutoRenewal from "@api/enableZuoraSubscriptionAutoRenewal";

export const deriveSubscriptionStatus = (subscription: Subscription): SubscriptionStatus => {
  const { type, autoRenew, paidThru, paused } = subscription;
  const isPast = paidThru ? new Date(paidThru * 1000) < new Date() : false;
  //TODO: figure out what a trialing subscription looks like and implement the logic here
  if (paused) return SUBSCRIPTION_STATUSES.PAUSED;
  if (type === SUBSCRIPTION_TYPES.COMP) return SUBSCRIPTION_STATUSES.COMPLIMENTARY;
  if (type === SUBSCRIPTION_TYPES.SUBSCRIPTION_SCHEDULE) return SUBSCRIPTION_STATUSES.SCHEDULED;
  if (autoRenew === true && isPast) return SUBSCRIPTION_STATUSES.PAST_DUE;
  if (autoRenew === false) return SUBSCRIPTION_STATUSES.CANCELLED;
  return SUBSCRIPTION_STATUSES.ACTIVE;
};

export const getSubscriptionStatusColors = (subscription: Subscription) => {
  const { semanticLayer, baseLayer } = tpTheme;
  switch (deriveSubscriptionStatus(subscription)) {
  case SUBSCRIPTION_STATUSES.PAST_DUE:
    return {
      bgcolor: semanticLayer.colors.error.background,
      color: semanticLayer.colors.error.main
    };
  case SUBSCRIPTION_STATUSES.CANCELLED:
  case SUBSCRIPTION_STATUSES.SCHEDULED:
    return {
      bgcolor: semanticLayer.colors.warning.background,
      color: semanticLayer.colors.warning.main
    };
  case SUBSCRIPTION_STATUSES.ACTIVE:
    return {
      bgcolor: semanticLayer.colors.success.background,
      color: semanticLayer.colors.success.main
    };
  case SUBSCRIPTION_STATUSES.TRIALING:
    return {
      bgcolor: semanticLayer.colors.success.background,
      color: semanticLayer.colors.success.main
    };
  case SUBSCRIPTION_STATUSES.COMPLIMENTARY:
    //using baselayer here because variables defined for these values in DT package don't seem to be working correctly
    return {
      bgcolor: baseLayer.colors.purple[50],
      color: baseLayer.colors.purple[700]
    };
  case SUBSCRIPTION_STATUSES.PAUSED:
    return {
      bgcolor: semanticLayer.colors.action.selectedBackground,
      color: semanticLayer.colors.text.secondary
    };
  default:
    return {
      bgcolor: semanticLayer.colors.success.background,
      color: semanticLayer.colors.success.main
    };
  }
};

export interface ManageMenuItem {
  label: string;
  destructive?: boolean;
  action?: () => void | Promise<void>;
  route?: string;
}

// eslint-disable-next-line max-len
const CANCEL_ERROR_MESSAGE = "Error cancelling subscription. Please try again or contact Customer Support for help";

export const PAYMENT_METHODS_ROUTE = "/payment-methods";

const isMobileSource = (source: Subscription["source"]) =>
  source === SUBSCRIPTION_SOURCES.APPLE || source === SUBSCRIPTION_SOURCES.GOOGLE;

export const getManageMenuItems = (
  subscription: Subscription,
  onSuccess?: (subscriptionIdentifier: string, updates: Partial<Subscription>) => void,
  onCancel?: () => void,
  onError?: (message: string) => void,
  onCancelSuccess?: (message: string) => void,
  segmentClickAction?: (feature: string, details?: string) => void,
  onCoachMatchCancel?: () => void
): ManageMenuItem[] => {
  const status = deriveSubscriptionStatus(subscription);
  const { source, recurringIntervalCount, recurringInterval } = subscription;

  if (
    status === SUBSCRIPTION_STATUSES.SCHEDULED
    || status === SUBSCRIPTION_STATUSES.COMPLIMENTARY
  ) {
    return [];
  }

  if (isMobileSource(source)) {
    return [];
  }

  const isStripe = source === SUBSCRIPTION_SOURCES.STRIPE;
  const isZuora = source === SUBSCRIPTION_SOURCES.ZUORA;

  if (!isStripe && !isZuora) {
    return [];
  }

  const paymentMethodsSegmentFeature = "Payment Methods in Manage dropdown button clicked";
  const paymentMethodsItem: ManageMenuItem = isStripe
    ? {
      label: "Payment Methods",
      action: () => {
        segmentClickAction?.(paymentMethodsSegmentFeature, subscription.subscriptionType);
        createStripeBillingPortalSession();
      },
    }
    : {
      label: "Payment Methods",
      route: PAYMENT_METHODS_ROUTE,
      action: () => segmentClickAction?.(paymentMethodsSegmentFeature, subscription.subscriptionType),
    };

  if (status === SUBSCRIPTION_STATUSES.PAST_DUE) {
    return [paymentMethodsItem];
  }

  const isActive = status === SUBSCRIPTION_STATUSES.ACTIVE;
  const isMonthly = recurringIntervalCount === PRODUCT_DURATIONS.MONTHLY
    && recurringInterval === RECURRING_INTERVALS.MONTH;
  const isAnnual = (recurringIntervalCount === PRODUCT_DURATIONS.ANNUAL
    && recurringInterval === RECURRING_INTERVALS.MONTH)
    || (recurringIntervalCount === 1 && recurringInterval === RECURRING_INTERVALS.YEAR);

  const cancelSegmentFeature = "Cancel Subscription in Manage dropdown button clicked";
  const isSelfPaidPremium = subscription.subscriptionType === SUBSCRIPTION_PRODUCT_TYPES.SELF_PAID_PREMIUM;
  const isCoachMatch = subscription.subscriptionType === SUBSCRIPTION_PRODUCT_TYPES.COACH_MATCH;
  const isStripeSelfPaidPremium = isStripe && isSelfPaidPremium;
  const isZuoraSelfPaidPremium = isZuora && isSelfPaidPremium;
  // A SelfPaidPremium subscription with no recurring interval had its latest invoice paid via a
  // one-time (stacked) purchase, so it isn't monthly/annual but can still be renewed.
  const isSelfPaidPremiumStackedPurchase = isSelfPaidPremium
    && recurringIntervalCount == null
    && recurringInterval == null;

  const cancelAction = async () => {
    segmentClickAction?.(cancelSegmentFeature, subscription.subscriptionType);

    if (isCoachMatch) {
      onCoachMatchCancel?.();
      return;
    }

    if (isStripe) {
      if (isStripeSelfPaidPremium && !subscription.isStackedSubscription) {
        onCancel?.();
        return;
      }
      const { subscriptionIdentifier } = subscription;
      if (!subscriptionIdentifier) {
        onError?.(CANCEL_ERROR_MESSAGE);
        return;
      }
      const stripeResponse = await cancelStripeSubscription(subscriptionIdentifier);
      if (!stripeResponse.success) {
        onError?.(CANCEL_ERROR_MESSAGE);
        return;
      }
      onSuccess?.(subscriptionIdentifier, { autoRenew: false });
      onCancelSuccess?.(stripeResponse.data?.message ?? "Your subscription has been cancelled successfully");
      return;
    }

    if (isZuora) {
      if (isZuoraSelfPaidPremium) {
        onCancel?.();
        return;
      }
      const zuoraResponse = await cancelZuoraAthletePremiumSubscription();
      if (!zuoraResponse.success) {
        onError?.(CANCEL_ERROR_MESSAGE);
        return;
      }
      if (subscription.subscriptionIdentifier) {
        onSuccess?.(subscription.subscriptionIdentifier, { autoRenew: false });
      }
      onCancelSuccess?.("Your subscription has been cancelled successfully");
      return;
    }

    rollbar.error(new Error("Tried to cancel invalid subscription type"), {
      subscriptionId: subscription.id,
      action: `cancel:${subscription.source}:${subscription.subscriptionType}`,
    });
    onError?.(CANCEL_ERROR_MESSAGE);
  };

  const canCancel = isStripe || isZuora;

  const autoRenewAction = async () => {
    const subscriptionIdentifier = subscription.subscriptionIdentifier;

    if (!subscriptionIdentifier) {
      throw new Error("Cannot enable auto-renew without a subscription identifier.");
    }

    segmentClickAction?.("Turn Auto-Renew On in Manage dropdown button clicked", subscription.subscriptionType);
    const response = isStripe
      ? await enableStripeSubscriptionAutoRenewal(subscriptionIdentifier)
      : await enableZuoraSubscriptionAutoRenewal(subscriptionIdentifier);
    if (response?.success) onSuccess?.(subscriptionIdentifier, { autoRenew: true });
  };

  const items: ManageMenuItem[] = [];

  if (isActive && canCancel) {
    items.push({ label: "Cancel Subscription", destructive: true, action: cancelAction });
  } else if (
    status === SUBSCRIPTION_STATUSES.CANCELLED
    && (isMonthly || isAnnual || isSelfPaidPremiumStackedPurchase)
  ) {
    items.push({ label: "Turn Auto-Renew On", action: autoRenewAction });
  }

  return items.length > 0 ? [paymentMethodsItem, ...items] : [];
};

const DURATION_LABELS: Record<string, string> = {
  [`${PRODUCT_DURATIONS.MONTHLY}_${RECURRING_INTERVALS.MONTH}`]: "MONTHLY",
  [`${PRODUCT_DURATIONS.QUARTERLY}_${RECURRING_INTERVALS.MONTH}`]: "QUARTERLY",
  [`${PRODUCT_DURATIONS.BI_ANNUAL}_${RECURRING_INTERVALS.MONTH}`]: "BI-ANNUAL",
  [`${PRODUCT_DURATIONS.ANNUAL}_${RECURRING_INTERVALS.MONTH}`]: "ANNUAL",
  [`1_${RECURRING_INTERVALS.YEAR}`]: "ANNUAL",
};

export const formatRenewalDate = (timestamp: number | undefined): string => {
  if (!timestamp) return "";
  return formatDate(timestamp, { month: "long", day: "numeric", year: "numeric" });
};

export const formatSubscriptionRenewalDateInfo = (
  subscription: Subscription,
  status: SubscriptionStatus,
  t: (key: string) => string
): string => {
  if (status === SUBSCRIPTION_STATUSES.CANCELLED) {
    return `${t("Auto-Renew Off")}: ${t("Access through")} ${formatRenewalDate(subscription.paidThru)}`;
  }
  if (subscription.type === SUBSCRIPTION_TYPES.COMP) {
    return `${t("Your complimentary access is active through")} ${formatRenewalDate(subscription.endDate)}`;
  }
  if (subscription.type === SUBSCRIPTION_TYPES.SUBSCRIPTION_SCHEDULE) {
    return `${t("Starts On")}: ${formatRenewalDate(subscription.startDate)}`;
  }
  return `${t("Next Renewal")}: ${formatRenewalDate(subscription.paidThru)}`;
};

export const getProductDurationLabel = (recurringIntervalCount?: number, recurringInterval?: string): string => {
  if (recurringIntervalCount == null || recurringInterval == null) return "";
  const key = `${recurringIntervalCount}_${recurringInterval}`;
  return DURATION_LABELS[key]
    ?? `EVERY ${recurringIntervalCount} ${recurringInterval.toUpperCase()}${recurringIntervalCount > 1 ? "S" : ""}`;
};
